import { Component, inject } from '@angular/core';
import { TranslocoDirective, TranslocoModule } from '@jsverse/transloco';
import { DividerWithTextComponent } from '../../../shared/components/divider-with-text/divider-with-text.component';
import { SelectComponent } from '../../../shared/components/select/select.component';
import { FormControl } from '@angular/forms';
import { InputFieldComponent } from '../../../shared/components/input-field/input-field.component';
import { CourseService } from '../../../domains/academy/course/services/course.service';
import { CourseDetailsDTO } from '../../../domains/academy/course/models/CourseDTO';
import { LanguageService } from '../../../core/services/language.service';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { CourseLibraryCardComponent } from '../../../domains/academy/course/components/course-library-card/course-library-card.component';
import { MatDialog } from '@angular/material/dialog';
import { LearningPlanBuilderDataService } from '../../../domains/academy/learning-plan/services/learning-plan-builder-data.service';
import { CourseDetailCardComponent } from '../../../domains/academy/course/components/course-detail-card/course-detail-card.component';
import { DIALOG_SIZE_LARGE } from '../../../shared/components/dialog/dialog-sizes';
import { AnalyticsService } from '../../../core/services/analytics/analytics.service';

@Component({
  selector: 'app-course-library',
  imports: [
    TranslocoModule,
    DividerWithTextComponent,
    SelectComponent,
    InputFieldComponent,
    TranslocoDirective,
    LoadingComponent,
    CourseLibraryCardComponent,
  ],
  templateUrl: './course-library.component.html',
})
export class CourseLibraryComponent {
  private readonly courseService = inject(CourseService);
  private readonly languageService = inject(LanguageService);
  private readonly dataService = inject(LearningPlanBuilderDataService);
  private readonly analyticsService = inject(AnalyticsService);
  dialog = inject(MatDialog);

  readonly topicAreaControl: FormControl<string | null> = new FormControl(
    'all',
  );
  readonly search: FormControl<string | null> = new FormControl('');
  public topicAreas: Array<{ key: string; value: string }> = [];
  public subscriptionCourses: Array<
    CourseDetailsDTO & { isRecommended: boolean }
  > = [];
  public filteredCourses: Array<CourseDetailsDTO & { isRecommended: boolean }> =
    [];
  public isLoadingTopicAreas = true;
  public isLoadingCourses = true;

  constructor() {
    this.topicAreaControl.valueChanges.subscribe(() => {
      this.applyFilters();
    });

    this.courseService.getCoursesOfSubscription().subscribe({
      next: (res) => {
        const disabledCourseNames = ['FitForOccupationalSafetyTestOut']; // Course soon to be removed from the library
        this.subscriptionCourses = res
          .filter((c) => !disabledCourseNames.includes(c.name))
          .map((c) =>
            Object.assign(c, {
              isRecommended: this.isCourseRecommended(c.name),
            }),
          );
        this.filteredCourses = this.subscriptionCourses;

        const uniqueTopicAreas = Array.from(
          new Set(this.subscriptionCourses.map((c) => c.topicAreaName)),
        ).map((name) => ({ key: name, value: name }));

        this.topicAreas = [
          { key: 'AllTopicAreas', value: 'all' },
          ...uniqueTopicAreas,
        ];
        this.topicAreaControl.setValue('all');
        this.isLoadingTopicAreas = false;

        this.isLoadingCourses = false;
        this.applyFilters();
      },
      error: () => {
        this.topicAreas = [{ key: 'AllTopicAreas', value: 'all' }];
        this.topicAreaControl.setValue('all');
        this.isLoadingTopicAreas = false;
        this.isLoadingCourses = false;
      },
    });

    this.search.valueChanges.subscribe(() => {
      this.applyFilters();
    });
  }

  isCourseRecommended(courseName: string): boolean {
    switch (courseName) {
      case 'CyberSecurity':
      case 'DataProtection':
      case 'OccupationalSafety':
      case 'Compliance':
      case 'CyberSecurityEUAIAct':
        return true;
      default:
        return false;
    }
  }

  get recommended(): Array<CourseDetailsDTO & { isRecommended: boolean }> {
    return this.filteredCourses.filter((c) => c.isRecommended);
  }

  get notRecommended(): Array<CourseDetailsDTO & { isRecommended: boolean }> {
    return this.filteredCourses.filter((c) => !c.isRecommended);
  }

  private applyFilters(): void {
    let filtered = this.subscriptionCourses;

    // Apply topic area filter
    const selectedTopicArea = this.topicAreaControl.value;
    if (selectedTopicArea && selectedTopicArea !== 'all') {
      filtered = filtered.filter(
        (course) => course.topicAreaName === selectedTopicArea,
      );
    }

    // Apply search filter
    const searchValue = this.search.value?.toLowerCase() ?? '';
    if (searchValue) {
      filtered = filtered.filter((course) => {
        const translatedName = this.languageService.translateOnRuntime(
          `portal.courses.courseTitleKeys.${course.name}.name`,
        );
        const translatedDescription = this.languageService.translateOnRuntime(
          `portal.courses.courseTitleKeys.${course.name}.description`,
        );
        return (
          translatedName.toLowerCase().includes(searchValue) ||
          translatedDescription.toLowerCase().includes(searchValue)
        );
      });
    }

    filtered = filtered.sort((a, b) => {
      if (a.isLocked === b.isLocked) {
        return 0;
      }
      return a.isLocked ? 1 : -1;
    });

    this.filteredCourses = filtered;
  }
  onOpenCourseCard(course: CourseDetailsDTO) {
    if (course.isLocked) {
      return;
    }
    this.analyticsService.track('course_card_opened', {
      courseName: course.name,
    });
    this.dataService.setDefaultTopicAreaId(course.topicAreaId);
    this.dataService.setOriginallySelectedCourse(course);
    this.dialog.open(CourseDetailCardComponent, {
      autoFocus: false,
      ...DIALOG_SIZE_LARGE,
      data: {
        course,
        isPrecuratedTraining: false,
      },
    });
  }
}
